import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw } from "lucide-react";

/**
 * Inline "Request a Remake" CTA that opens a small modal form.
 * Pass the product object (needs at least id + name).
 */
export default function RemakeRequestButton({ product }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setOpen(false);
    setForm({ customer_name: "", customer_email: "", notes: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/remake-requests", { ...form, product_id: product.id });
      toast.success("Request received. David will get in touch when this piece is back in the workshop.");
      close();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-primary flex-1 justify-center"
        data-testid="request-remake-btn"
      >
        <RefreshCw size={14} /> Request a Remake
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="remake-modal">
          <form
            onSubmit={submit}
            className="bg-[#E0F2F1] sm:border sm:border-[#B2DFDB] w-full sm:max-w-md flex flex-col"
          >
            <div className="p-5 sm:p-6 border-b border-[#B2DFDB] flex justify-between items-start gap-3">
              <div>
                <p className="smallcaps mb-1">Sold Out — Request a Remake</p>
                <h2 className="font-display text-2xl text-[#212121] leading-tight">{product.name}</h2>
              </div>
              <button type="button" onClick={close} className="text-[#757575] p-2 -mt-1 -mr-2" aria-label="Close" data-testid="remake-close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 sm:p-6 space-y-4">
              <p className="text-[#757575] text-sm leading-relaxed">
                David batches new pieces a few times a year. Drop your details and he&apos;ll email you when this one&apos;s back in the workshop.
              </p>
              <div>
                <label className="smallcaps block mb-2">Your name</label>
                <input
                  required
                  className="input-light"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  data-testid="remake-name"
                />
              </div>
              <div>
                <label className="smallcaps block mb-2">Email</label>
                <input
                  type="email"
                  required
                  className="input-light"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  data-testid="remake-email"
                />
              </div>
              <div>
                <label className="smallcaps block mb-2">Notes (optional)</label>
                <textarea
                  rows={3}
                  className="input-light"
                  placeholder="Anything specific you'd like David to know about your version?"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="remake-notes"
                />
              </div>
            </div>
            <div className="p-5 sm:p-6 border-t border-[#B2DFDB] flex justify-end gap-3">
              <button type="button" onClick={close} className="btn-ghost px-4">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="remake-submit">
                {submitting ? "Sending…" : "Send request"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
